// src/store/modules/landing.js
import { API } from '@/config/api.js'
import { replaceIdsWithUids } from '@/utils/id-generate'
import { safeClone, safeRead, safeWrite } from '@/utils/clone.js'
import { getLandingWidth } from '@/config/screen-sizes.js'

const API_BASE = `${API.fullUrl}/landing`

const DEFAULT_FALLBACK = {
  id: '100',
  uid: null,
  bg_color: 'transparent',
  font: [],
  logo: null,
  size: null,
  user_uid: '123123',
  title: '123123',
  preview: '123123',
  url: '123123',
  url_preview: '123123',
  seo: {
    meta_title: '',
    meta_description: '',
    meta_keywords: [],
    canonical: null,
    robots: 'index,follow',
    noindex_until_published: false,
    open_graph: {},
    twitter: {},
    structured_data: { enabled: false, json_ld: {} },
    hreflang: [],
    sitemap: { included: false, priority: 0.6, changefreq: 'weekly' },
    meta_robots_directives: { noarchive: false, nosnippet: false, noimageindex: false },
    updated_at: null
  }
}

function isDefaultSettings(obj) {
  if (!obj) return true
  if (obj.uid === DEFAULT_FALLBACK.uid) return true
  if ((obj.title && obj.title === DEFAULT_FALLBACK.title) || (obj.url && obj.url === DEFAULT_FALLBACK.url)) return true
  return false
}

/**
 * handleFetch helper (uses fetch)
 */
async function handleFetch(url, options = {}) {
  const opts = { method: 'GET', headers: {}, ...options }

  if (opts.body && typeof opts.body === 'object' && !(opts.body instanceof FormData)) {
    opts.headers = { 'Content-Type': 'application/json', ...opts.headers }
    opts.body = JSON.stringify(opts.body)
  }

  let resp
  try {
    resp = await fetch(url, opts)
  } catch (err) {
    const e = new Error('Network error: ' + (err && err.message ? err.message : String(err)))
    e.body = null
    e.status = 0
    throw e
  }

  const text = await resp.text()
  let parsed = null
  if (text) {
    try {
      parsed = JSON.parse(text)
    } catch (e) {
      parsed = text
    }
  }

  if (!resp.ok) {
    const msg = (parsed && (parsed.message || parsed.error || parsed.err)) || resp.statusText || `HTTP ${resp.status}`
    const err = new Error(msg)
    err.status = resp.status
    err.body = parsed
    throw err
  }

  if (parsed && typeof parsed === 'object') {
    if (parsed.data !== undefined) return parsed.data
    if (parsed.result !== undefined) return parsed.result
  }
  return parsed
}

// helper: попытка получить реальную ширину/высоту viewport
function readViewport() {
  if (typeof window === 'undefined') return { width: 0, height: 0 }
  if (window.visualViewport) {
    return { width: Math.round(window.visualViewport.width), height: Math.round(window.visualViewport.height) }
  }
  return {
    width: Math.round(document.documentElement.clientWidth || window.innerWidth || 0),
    height: Math.round(document.documentElement.clientHeight || window.innerHeight || 0)
  }
}

export default {
  namespaced: true,
  state: {
    landing_settings: safeRead('landing_settings', DEFAULT_FALLBACK),
    landing: safeRead('landing_data', []),
    loading: false,
    error: null,
    // синхронизация
    syncedWithServer: false,     // settings synced
    landingDataSynced: false     // landing data synced
  },
  getters: {
    landing: (state) => state.landing,
    landingData: (state) => state.landing,
    landingSettings: (state) => state.landing_settings,
    isDefaultSettings: (state) => isDefaultSettings(state.landing_settings),
    loading: (state) => state.loading,
    error: (state) => state.error,
    syncedWithServer: (state) => state.syncedWithServer,
    landingDataSynced: (state) => state.landingDataSynced
  },
  mutations: {
    SET_LOADING(state, v) { state.loading = !!v },
    SET_ERROR(state, v) { state.error = v ? String(v) : null },

    // local edit of settings (does NOT auto-send to server)
    SET_LANDING_SETTINGS(state, data) {
      state.landing_settings = {
        ...state.landing_settings,
        ...data
      }
      safeWrite('landing_settings', state.landing_settings)
      state.syncedWithServer = false
    },

    // replace settings with server data (mark synced)
    SET_LANDING_SETTINGS_FROM_SERVER(state, data) {
      state.landing_settings = {
        ...DEFAULT_FALLBACK,
        ...data
      }
      safeWrite('landing_settings', state.landing_settings)
      state.syncedWithServer = true
      state.error = null
    },

    // local set landing (local edits) -> mark landingDataSynced = false
    SET_LANDING(state, data) {
      state.landing = data || []
      safeWrite('landing_data', state.landing)
      state.landingDataSynced = false
    },

    // set landing from server -> mark synced true
    SET_LANDING_FROM_SERVER(state, data) {
      state.landing = data || []
      safeWrite('landing_data', state.landing)
      state.landingDataSynced = true
      state.error = null
    },

    // all local mutations below mark landingDataSynced = false
    REMOVE_ELEMENT_BY_ID(state, id) {
      if (!id) {
        console.warn('REMOVE_ELEMENT_BY_ID called without id', id);
        return;
      }

      const clone = safeClone(state.landing || []);
      let removed = false;

      function removeRecursive(arr) {
        if (!Array.isArray(arr)) return false;

        for (let i = arr.length - 1; i >= 0; i--) {
          const item = arr[i];
          if (!item) continue;

          if (item.id === id) {
            arr.splice(i, 1);
            removed = true;
            return true;
          }

          let deletedInside = false;

          if (item.children?.data && Array.isArray(item.children.data)) {
            const r = removeRecursive(item.children.data);
            if (r) deletedInside = true;
            if (r && (!Array.isArray(item.children.data) || item.children.data.length === 0)) {
              arr.splice(i, 1);
              removed = true;
              return true;
            }
          }

          if (!deletedInside && Array.isArray(item.data)) {
            const r = removeRecursive(item.data);
            if (r) deletedInside = true;
            if (r && (!Array.isArray(item.data) || item.data.length === 0)) {
              arr.splice(i, 1);
              removed = true;
              return true;
            }
          }

          if (!deletedInside && Array.isArray(item.list)) {
            const r = removeRecursive(item.list);
            if (r) deletedInside = true;
            if (r && (!Array.isArray(item.list) || item.list.length === 0)) {
              arr.splice(i, 1);
              removed = true;
              return true;
            }
          }
        }

        return false;
      }

      removeRecursive(clone);

      if (removed) {
        state.landing = clone;
        safeWrite('landing_data', state.landing);
        state.landingDataSynced = false;
      } else {
        console.warn('REMOVE_ELEMENT_BY_ID: element not found, id=', id);
      }
    },


    UPDATE_ELEMENT_PROP(state, { id, key, value }) {
      const updateRecursive = (items) =>
        items.map(item => {
          if (!item) return item
          if (item.id === id) {
            return {
              ...item,
              settings: {
                ...item.settings,
                [key]: value
              }
            }
          }
          if (item.children?.data) {
            return { ...item, children: { ...item.children, data: updateRecursive(item.children.data) } }
          }
          if (Array.isArray(item.data)) {
            return { ...item, data: updateRecursive(item.data) }
          }
          if (Array.isArray(item.list)) {
            return { ...item, list: updateRecursive(item.list) }
          }
          return item
        })

      state.landing = updateRecursive(safeClone(state.landing || []))
      safeWrite('landing_data', state.landing)
      state.landingDataSynced = false
    },

    REPLACE_ELEMENT_BY_ID(state, updatedElement) {
      if (!updatedElement?.id) {
        console.warn('REPLACE_ELEMENT_BY_ID called without id', updatedElement)
        return
      }
      const clone = safeClone(state.landing || [])
      let replaced = false
      function replaceRecursive(arr) {
        if (!Array.isArray(arr)) return false
        for (let i = 0; i < arr.length; i++) {
          const item = arr[i]
          if (!item) continue
          if (item.id === updatedElement.id) {
            arr.splice(i, 1, safeClone(updatedElement))
            replaced = true
            return true
          }
          if (item.children?.data && replaceRecursive(item.children.data)) return true
          if (Array.isArray(item.data) && replaceRecursive(item.data)) return true
          if (Array.isArray(item.list) && replaceRecursive(item.list)) return true
        }
        return false
      }
      replaceRecursive(clone)
      if (replaced) {
        state.landing = clone
        safeWrite('landing_data', state.landing)
        state.landingDataSynced = false
      } else console.warn('REPLACE_ELEMENT_BY_ID: element not found, id=', updatedElement.id)
    },

    SET_GROUP(state, payload) {
      if (!payload?.id) {
        console.warn('SET_GROUP called without id', payload)
        return
      }
      const clone = safeClone(state.landing || [])
      let found = false
      function replaceGroup(arr) {
        if (!Array.isArray(arr)) return
        for (let i = 0; i < arr.length; i++) {
          if (!arr[i]) continue
          if (arr[i].id === payload.id) {
            arr.splice(i, 1, { ...arr[i], ...payload })
            found = true
            return
          }
          if (arr[i].children?.data) {
            replaceGroup(arr[i].children.data)
            if (found) return
          }
          if (Array.isArray(arr[i].data)) {
            replaceGroup(arr[i].data)
            if (found) return
          }
          if (Array.isArray(arr[i].list)) {
            replaceGroup(arr[i].list)
            if (found) return
          }
        }
      }
      replaceGroup(clone)
      if (found) {
        state.landing = clone
        safeWrite('landing_data', state.landing)
        state.landingDataSynced = false
      } else console.warn('SET_GROUP: group not found id=', payload.id)
    },

    SET_ELEMENT(state, payload) {
      if (!payload?.id) {
        console.warn('SET_ELEMENT called without id', payload)
        return
      }
      const clone = safeClone(state.landing || [])
      let replaced = false
      function replaceArr(arr) {
        if (!Array.isArray(arr)) return false
        for (let i = 0; i < arr.length; i++) {
          const item = arr[i]
          if (!item) continue
          if (item.id === payload.id) {
            arr.splice(i, 1, safeClone(payload))
            replaced = true
            return true
          }
          if (item.children?.data && replaceArr(item.children.data)) return true
          if (Array.isArray(item.data) && replaceArr(item.data)) return true
          if (Array.isArray(item.list) && replaceArr(item.list)) return true
        }
        return false
      }
      replaceArr(clone)
      if (replaced) {
        state.landing = clone
        safeWrite('landing_data', state.landing)
        state.landingDataSynced = false
      } else console.warn('SET_ELEMENT: element not found id=', payload.id)
    }
  },
  actions: {
    // Fetch landing_settings from server by domain (tries normalized domain)
    async fetchLandingSettingsByDomain({ commit, dispatch }, { domain } = {}) {
      if (!domain) throw new Error('domain required')
      commit('SET_LOADING', true)
      try {
        // Нормализация домена: убираем протокол, путь, порт, приводим к lower-case
        let d = String(domain).trim().toLowerCase()
        d = d.replace(/^https?:\/\//, '').replace(/^\/\//, '')
        d = d.split('/')[0]
        d = d.split(':')[0]
        d = d.replace('.ru', '')
        
        const url = `${API_BASE}/landing-domain/${encodeURIComponent(d)}`
        const data = await handleFetch(url, { method: 'GET' })
        if (!data) throw new Error('No data from server')

        // Сохраняем settings
        commit('SET_LANDING_SETTINGS_FROM_SERVER', data)

        // Если в settings отсутствует size — установим его тут по текущему viewport,
        // чтобы компоненты, читающие landing_settings.size, сразу получили корректное значение.
        try {
          const { width, height } = readViewport()
          if ((!data.size || data.size === null) && width > 0) {
            const landingWidth = getLandingWidth(Number(width))
            commit('SET_LANDING_SETTINGS', { size: { width: landingWidth, height } })
          }
        } catch (e) {
          console.warn('Failed to set default size for landing settings', e)
        }

        const uid = data.uid
        if (uid) {
          try {
            await dispatch('fetchLandingDataFromServer', { landing_settings_uid: uid })
          } catch (e) {
            console.warn('fetchLandingDataFromServer after domain fetch failed', e)
          }
        }
        return data
      } catch (err) {
        console.error('fetchLandingSettingsByDomain error', err)
        commit('SET_ERROR', err && err.message ? err.message : String(err))
        throw err
      } finally {
        commit('SET_LOADING', false)
      }
    },

    // Convenience: fetch by current host (window.location.host) — useful on client-side init
    async fetchLandingSettingsByHost({ dispatch, commit }) {
      if (typeof window === 'undefined' || !window.location) {
        throw new Error('No window.location available (not running in browser)')
      }
      const host = window.location.host
      // host может содержать порт — normalize будет выполнён в fetchLandingSettingsByDomain
      return dispatch('fetchLandingSettingsByDomain', { domain: host })
    },

    // Инициализация settings + landing: если локальные дефолтные/пустые — подгружаем с сервера
    async initLandingSettings({ state, commit, dispatch }, { uid } = {}) {
      const candidateUid = uid || (state.landing_settings && state.landing_settings.uid)
      if (!candidateUid) return

      commit('SET_LOADING', true)
      try {
        // если settings дефолтные — подгружаем их
        if (isDefaultSettings(state.landing_settings) || !state.landing_settings || !state.landing_settings.uid) {
          try {
            await dispatch('fetchLandingSettingsFromServer', { uid: candidateUid })
          } catch (e) {
            commit('SET_ERROR', e.message || 'fetch settings failed')
            // не бросаем — попытаемся подгрузить landing data если возможно
          }
        }

        // если landing пустой -> подгружаем landing_data
        try {
          await dispatch('fetchLandingDataFromServer', { landing_settings_uid: candidateUid })
        } catch (e) {
          console.warn('fetchLandingDataFromServer failed', e)
        }

      } finally {
        commit('SET_LOADING', false)
      }
    },

    // Fetch landing_settings from server by uid and commit SET_LANDING_SETTINGS_FROM_SERVER
    async fetchLandingSettingsFromServer({ commit, dispatch }, { uid }) {
      if (!uid) throw new Error('uid required')
      commit('SET_LOADING', true)
      try {
        const url = `${API_BASE}/landing-uid/${encodeURIComponent(uid)}`
        const data = await handleFetch(url, { method: 'GET' })
        if (!data) throw new Error('No data from server')
        commit('SET_LANDING_SETTINGS_FROM_SERVER', data)

        // если size отсутствует — устанавливаем по текущему viewport
        try {
          const { width, height } = readViewport()
          if ((!data.size || data.size === null) && width > 0) {
            const landingWidth = getLandingWidth(Number(width))
            commit('SET_LANDING_SETTINGS', { size: { width: landingWidth, height } })
          }
        } catch (e) {
          console.warn('Failed to set default size for landing settings', e)
        }

        return data
      } catch (err) {
        console.error('fetchLandingSettingsFromServer error', err)
        commit('SET_ERROR', err && err.message ? err.message : String(err))
        throw err
      } finally {
        commit('SET_LOADING', false)
      }
    },

    // Fetch landing_data for given landing_settings_uid
    // GET /landing/:landing_settings_uid/data
    async fetchLandingDataFromServer({ commit, state, dispatch }, { landing_settings_uid }) {
      if (!landing_settings_uid) throw new Error('landing_settings_uid required')
      commit('SET_LOADING', true)
      try {
        const url = `${API_BASE}/landing/${encodeURIComponent(landing_settings_uid)}/data`
        const row = await handleFetch(url, { method: 'GET' })
        // row might be null (no data yet) or object { id, uid, landing_settings_uid, data, ... }
        if (!row) {
          commit('SET_LANDING_FROM_SERVER', [])
          return null
        }
        // If row has .data field -> that's the actual landing array/object
        const landingPayload = (row && row.data !== undefined) ? row.data : row
        commit('SET_LANDING_FROM_SERVER', landingPayload || [])

        // Если в settings нет size — зададим его немедленно, чтобы компоненты сразу получили размер
        try {
          const currentSize = (state && state.landing_settings && state.landing_settings.size) || null
          if (!currentSize) {
            const { width, height } = readViewport()
            if (width > 0) {
              const landingWidth = getLandingWidth(Number(width))
              // локально обновляем settings.size — не шлём на сервер
              commit('SET_LANDING_SETTINGS', { size: { width: landingWidth, height } })
            }
          }
        } catch (e) {
          console.warn('Failed to ensure landing_settings.size after landing load', e)
        }

        // Также попробуем принудительно обновить device (на всякий случай)
        try {
          await dispatch('device/updateDevice', null, { root: true })
        } catch (e) {
          // не критично
        }

        return landingPayload || null
      } catch (err) {
        console.error('fetchLandingDataFromServer error', err)
        commit('SET_ERROR', err && err.message ? err.message : String(err))
        throw err
      } finally {
        commit('SET_LOADING', false)
      }
    },

    // локальное обновление settings — только локально
    UPDATE_LANDING_SETTINGS({ commit }, data) {
      commit('SET_LANDING_SETTINGS', data)
    },

    // Явная выгрузка landing_settings в БД (PUT/POST)
    async uploadLandingSettingsToServer({ state, commit }) {
      commit('SET_LOADING', true)
      try {
        const payload = { ...state.landing_settings }
        if (payload.id && payload.id !== '100') {
          const url = `${API_BASE}/landing/${encodeURIComponent(payload.id)}`
          const data = await handleFetch(url, { method: 'PUT', body: payload })
          if (data) commit('SET_LANDING_SETTINGS_FROM_SERVER', data)
          return data
        } else {
          const url = `${API_BASE}/add-settings`
          const data = await handleFetch(url, { method: 'POST', body: payload })
          if (data) commit('SET_LANDING_SETTINGS_FROM_SERVER', data)
          return data
        }
      } catch (err) {
        console.error('uploadLandingSettingsToServer error', err)
        commit('SET_ERROR', err && err.message ? err.message : String(err))
        throw err
      } finally {
        commit('SET_LOADING', false)
      }
    },

    // Отдельно: выгрузка SEO (POST /landing/:uid/seo)
    // uploadSeoToServer (store action)
    async uploadSeoToServer({ state, commit }, { updated_by = null, seo = null } = {}) {
      commit('SET_LOADING', true)
      try {
        const uid = state.landing_settings && state.landing_settings.uid
        if (!uid) throw new Error('landing_settings uid missing')

        // Helper: default seo shape
        function getDefaultSeo() {
          return {
            meta_title: '',
            meta_description: '',
            meta_keywords: [],
            canonical: '',
            robots: 'index,follow',
            noindex_until_published: false,
            open_graph: { 'og:title': '', 'og:description': '', 'og:image': '', 'og:image:width': 1200, 'og:image:height': 630, 'og:type': 'website' },
            twitter: { card: 'summary_large_image', site: '', title: '', image: '' },
            structured_data: { enabled: false, json_ld: {} },
            hreflang: [],
            sitemap: { included: false, priority: 0.6, changefreq: 'weekly' },
            meta_robots_directives: { noarchive: false, nosnippet: false, noimageindex: false },
            updated_at: new Date().toISOString()
          }
        }

        // choose source: explicit param seo -> state -> default
        const src = seo || (state.landing_settings && state.landing_settings.seo) || {}
        const def = getDefaultSeo()

        // Normalize: ensure all keys exist and are not null (use defaults where missing)
        const seoPayload = {
          meta_title: (src.meta_title !== undefined && src.meta_title !== null) ? src.meta_title : def.meta_title,
          meta_description: (src.meta_description !== undefined && src.meta_description !== null) ? src.meta_description : def.meta_description,
          meta_keywords: Array.isArray(src.meta_keywords) ? src.meta_keywords : def.meta_keywords,
          canonical: (src.canonical !== undefined && src.canonical !== null) ? src.canonical : def.canonical,
          robots: src.robots || def.robots,
          noindex_until_published: !!src.noindex_until_published,
          open_graph: Object.assign({}, def.open_graph, src.open_graph || {}),
          twitter: Object.assign({}, def.twitter, src.twitter || {}),
          structured_data: {
            enabled: !!(src.structured_data && src.structured_data.enabled),
            json_ld: (src.structured_data && src.structured_data.json_ld) ? src.structured_data.json_ld : def.structured_data.json_ld
          },
          hreflang: Array.isArray(src.hreflang) ? src.hreflang : def.hreflang,
          sitemap: Object.assign({}, def.sitemap, src.sitemap || {}),
          meta_robots_directives: Object.assign({}, def.meta_robots_directives, src.meta_robots_directives || {}),
          updated_at: (src.updated_at && typeof src.updated_at === 'string') ? src.updated_at : new Date().toISOString(),
          updated_by: updated_by || null
        }

        // POST to server
        const url = `${API_BASE}/landing/${encodeURIComponent(uid)}/seo`
        const data = await handleFetch(url, { method: 'POST', body: seoPayload })

        // если сервер вернул seo-объект, сохраняем его в store (нормализуя)
        if (data) {
          const serverSeo = Object.assign({}, getDefaultSeo(), data)
          commit('SET_LANDING_SETTINGS_FROM_SERVER', { ...state.landing_settings, seo: serverSeo })
        }

        return data
      } catch (err) {
        console.error('uploadSeoToServer error', err)
        commit('SET_ERROR', err && err.message ? err.message : String(err))
        throw err
      } finally {
        commit('SET_LOADING', false)
      }
    },


    // -----------------------
    // NEW: Landing data upload (explicit)
    // POST /landing/:landing_settings_uid/data  (UPSERT)
    async uploadLandingDataToServer({ state, commit }, { landing_settings_uid = null, updated_by = null } = {}) {
      commit('SET_LOADING', true)
      try {
        const uid = landing_settings_uid || (state.landing_settings && state.landing_settings.uid)
        if (!uid) throw new Error('landing_settings uid missing')

        const payload = {
          data: state.landing,
          updated_by
        }

        const url = `${API_BASE}/landing/${encodeURIComponent(uid)}/data`
        const saved = await handleFetch(url, { method: 'POST', body: payload })

        // saved may be record with .data — extract actual landing content
        const landingPayload = (saved && saved.data !== undefined) ? saved.data : saved
        commit('SET_LANDING_FROM_SERVER', landingPayload || [])

        // ensure settings.size again in case server returned record without size
        try {
          const { width, height } = readViewport()
          if ((!state.landing_settings || !state.landing_settings.size) && width > 0) {
            const landingWidth = getLandingWidth(Number(width))
            commit('SET_LANDING_SETTINGS', { size: { width: landingWidth, height } })
          }
        } catch (e) {
          console.warn('Failed to set size after uploadLandingDataToServer', e)
        }

        return saved
      } catch (err) {
        console.error('uploadLandingDataToServer error', err)
        commit('SET_ERROR', err && err.message ? err.message : String(err))
        throw err
      } finally {
        commit('SET_LOADING', false)
      }
    },

    // DELETE landing data by landing_settings_uid (explicit)
    async deleteLandingDataOnServer({ commit }, { landing_settings_uid } = {}) {
      if (!landing_settings_uid) throw new Error('landing_settings_uid required')
      commit('SET_LOADING', true)
      try {
        const url = `${API_BASE}/landing/${encodeURIComponent(landing_settings_uid)}/data`
        const res = await handleFetch(url, { method: 'DELETE' })
        // reset local landing to empty array
        commit('SET_LANDING_FROM_SERVER', [])
        return res
      } catch (err) {
        console.error('deleteLandingDataOnServer error', err)
        commit('SET_ERROR', err && err.message ? err.message : String(err))
        throw err
      } finally {
        commit('SET_LOADING', false)
      }
    },

    // -----------------------
    // local-only changes to landing content (no auto-upload)
    DELETE_ELEMENT({ commit }, id) {
      if (!id) {
        console.warn('DELETE_ELEMENT called without id', id);
        return;
      }
      commit('REMOVE_ELEMENT_BY_ID', id);
    },
    UPDATE_ELEMENT_PROP({ commit }, payload) { commit('UPDATE_ELEMENT_PROP', payload) },
    UPDATE_INDEXES({ commit, state }) {
      try { commit('SET_LANDING', replaceIdsWithUids(state.landing)) } catch (e) { console.warn('UPDATE_INDEXES failed', e) }
    },
    UPDATE_LANDING({ commit }, data) { commit('SET_LANDING', data) },
    UPDATE_GROUP({ commit }, group) { commit('SET_GROUP', group) },
    UPDATE_ELEMENT({ commit }, element) {
      if (!element?.id) { console.warn('UPDATE_ELEMENT called without id', element); return }
      commit('REPLACE_ELEMENT_BY_ID', safeClone(element))
    },
    SET_ELEMENT({ commit }, element) { commit('SET_ELEMENT', element) }
  }
}
